import React, { useCallback, useEffect, useRef, useState } from 'react';
import { load } from '@cashfreepayments/cashfree-js';
import { ShieldCheck, CheckCircle2, AlertTriangle, XCircle, Loader2, X, ArrowRight, Clock } from 'lucide-react';
import { partnerPlanApi, money, fmtDate, INTERVALS, KIND_LABEL } from './partnerPlanApi';

// One SDK instance per mode (sandbox / production), loaded lazily
const sdkCache = {};
const getCashfree = (mode) => {
  if (!sdkCache[mode]) sdkCache[mode] = load({ mode });
  return sdkCache[mode];
};

const wait = (ms) => new Promise(r => setTimeout(r, ms));
const VERIFY_DELAYS = [0, 1500, 2000, 2500, 3000, 3000, 4000, 5000];

export const Modal = ({ title, onClose, children, footer, size = 'md', dismissible = true, labelledBy }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && dismissible) onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose, dismissible]);

  return (
    <div className="pp-overlay" onMouseDown={(e) => dismissible && e.target === e.currentTarget && onClose()}>
      <div className={`pp-modal pp-modal--${size}`} role="dialog" aria-modal="true" aria-labelledby={labelledBy}>
        {title && (
          <div className="pp-modal__head">
            <h3 id={labelledBy}>{title}</h3>
            {dismissible && <button className="pp-icon-btn" onClick={onClose} aria-label="Close"><X size={18} /></button>}
          </div>
        )}
        <div className="pp-modal__body">{children}</div>
        {footer && <div className="pp-modal__foot">{footer}</div>}
      </div>
    </div>
  );
};

const Line = ({ label, value, muted, strong, negative, hint }) => (
  <div className={`pp-line ${strong ? 'pp-line--strong' : ''} ${muted ? 'pp-line--muted' : ''}`}>
    <span>{label}{hint && <small>{hint}</small>}</span>
    <span className={negative ? 'pp-line__neg' : ''}>{value}</span>
  </div>
);

export const QuoteBreakdown = ({ q }) => {
  const c = q.quote.currency;
  return (
    <div className="pp-breakdown">
      <Line label={`${q.planName} · ${INTERVALS[q.interval]?.label}`} value={money(q.quote.baseMinor, c)} />
      {q.quote.setupFeeMinor > 0 && <Line label="One-time setup fee" value={money(q.quote.setupFeeMinor, c)} />}
      {q.quote.discountMinor > 0 && <Line label="Discount" value={`− ${money(q.quote.discountMinor, c)}`} negative />}
      {q.quote.prorationCreditMinor > 0 && (
        <Line label="Credit for unused time" hint="on your current plan" value={`− ${money(q.quote.prorationCreditMinor, c)}`} negative />
      )}
      {q.quote.taxMode === 'exclusive'
        ? <Line label={`GST (${q.quote.gstRatePercent}%)`} value={money(q.quote.taxMinor, c)} muted />
        : <Line label={`Includes GST (${q.quote.gstRatePercent}%)`} value={money(q.quote.taxMinor, c)} muted />}
      <div className="pp-breakdown__rule" />
      <Line label="Total due today" value={money(q.quote.totalMinor, c, { decimals: 2 })} strong />
      {q.quote.waivedMinor > 0 && <p className="pp-note">A balance of {money(q.quote.waivedMinor, c)} has been waived — it’s below the minimum charge.</p>}
      {q.quote.unusedCreditMinor > 0 && <p className="pp-note">{money(q.quote.unusedCreditMinor, c)} of unused credit exceeds the new plan price and can’t be carried forward.</p>}
    </div>
  );
};

/**
 * request: { planId, planVersion?, interval }
 * Handles quote → confirm → Cashfree checkout → server verification.
 */
const PlanCheckoutModal = ({ request, onClose, onDone }) => {
  const [stage, setStage] = useState('loading');   // loading | review | paying | verifying | success | pending | failed | review_needed | error
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [payment, setPayment] = useState(null);
  const mounted = useRef(true);
  const busy = ['paying', 'verifying'].includes(stage);

  useEffect(() => () => { mounted.current = false; }, []);

  const loadQuote = useCallback(async () => {
    setStage('loading');
    setError('');
    try {
      const q = await partnerPlanApi.quote(request);
      if (!mounted.current) return;
      setQuote(q);
      setStage('review');
    } catch (err) {
      if (!mounted.current) return;
      setError(err.message);
      setStage('error');
    }
  }, [request]);

  useEffect(() => { loadQuote(); }, [loadQuote]);

  const verifyUntilSettled = async (orderId, checkoutResult) => {
    setStage('verifying');
    let last = null;
    for (const delay of VERIFY_DELAYS) {
      await wait(delay);
      if (!mounted.current) return;
      try {
        last = (await partnerPlanApi.verify(orderId)).payment;
      } catch { /* transient — keep trying */ }
      if (!last) continue;
      setPayment(last);
      if (last.status === 'fulfilled') { setStage('success'); onDone?.(); window.dispatchEvent(new CustomEvent('rp:plan-changed')); return; }
      if (last.status === 'refund_required') { setStage('review_needed'); onDone?.(); return; }
      if (['expired', 'cancelled', 'failed'].includes(last.status)) { setStage('failed'); return; }
      // Checkout closed without paying → no point polling long
      if (last.status === 'created' && checkoutResult?.error) { setStage('failed'); return; }
    }
    setStage(last?.status === 'created' ? 'failed' : 'pending');
    onDone?.();
  };

  const pay = async () => {
    if (busy) return;
    setStage('paying');
    setError('');
    setNotice('');
    try {
      const res = await partnerPlanApi.checkout({
        planId: quote.planId,
        planVersion: quote.planVersion,
        interval: quote.interval,
        expectedTotalMinor: quote.quote.totalMinor,
      });

      if (res.fulfilled) {   // fully covered by credit
        setPayment(res.payment);
        setStage('success');
        onDone?.();
        return;
      }

      const cashfree = await getCashfree(res.cashfreeMode);
      const result = await cashfree.checkout({ paymentSessionId: res.paymentSessionId, redirectTarget: '_modal' });
      await verifyUntilSettled(res.orderId, result);
    } catch (err) {
      if (!mounted.current) return;
      if (err.data?.code === 'PRICE_CHANGED' && err.data.quote) {
        setQuote(err.data.quote);
        setAgreed(false);
        setNotice('The amount was updated since you opened this. Please review the new total.');
        setStage('review');
        return;
      }
      setError(err.message || 'Could not start the payment.');
      setStage('review');
    }
  };

  const fromPlan = quote?.fromPlan;
  const isRenewal = quote?.kind === 'renewal';
  const title = { new: 'Confirm subscription', upgrade: 'Confirm upgrade', renewal: 'Renew your plan', downgrade: 'Change plan' }[quote?.kind] || 'Checkout';

  return (
    <Modal
      title={['success', 'pending', 'failed', 'review_needed'].includes(stage) ? null : title}
      onClose={onClose}
      dismissible={!busy}
      labelledBy="pp-checkout-title"
      footer={stage === 'review' && quote && (
        <>
          <button className="pp-btn pp-btn--ghost" onClick={onClose}>Cancel</button>
          <button className="pp-btn pp-btn--primary" onClick={pay} disabled={!agreed}>
            <ShieldCheck size={16} /> Pay {money(quote.quote.totalMinor, quote.quote.currency, { decimals: 2 })}
          </button>
        </>
      )}
    >
      {stage === 'loading' && <div className="pp-center"><Loader2 className="pp-spin" size={28} /><p>Preparing your quote…</p></div>}

      {stage === 'error' && (
        <div className="pp-result">
          <AlertTriangle size={40} className="pp-result__icon pp-result__icon--warn" />
          <h4>We couldn’t prepare this checkout</h4>
          <p>{error}</p>
          <div className="pp-result__actions">
            <button className="pp-btn pp-btn--ghost" onClick={onClose}>Close</button>
            <button className="pp-btn pp-btn--primary" onClick={loadQuote}>Try again</button>
          </div>
        </div>
      )}

      {(stage === 'review' || stage === 'paying') && quote && (
        <div className="pp-checkout">
          {notice && <div className="pp-banner pp-banner--warn"><AlertTriangle size={16} />{notice}</div>}
          {error && <div className="pp-banner pp-banner--bad"><XCircle size={16} />{error}</div>}

          {fromPlan && !isRenewal && (
            <div className="pp-switch">
              <div><small>Current</small><strong>{fromPlan.planName}</strong><span>{INTERVALS[fromPlan.interval]?.label}</span></div>
              <ArrowRight size={18} />
              <div className="pp-switch__to"><small>New</small><strong>{quote.planName}</strong><span>{INTERVALS[quote.interval]?.label}</span></div>
            </div>
          )}

          <div className="pp-period">
            <Clock size={15} />
            <span>
              {isRenewal ? 'Covers' : 'Billing period'}: <strong>{fmtDate(quote.quote.periodStart)}</strong> – <strong>{fmtDate(quote.quote.periodEnd)}</strong>
            </span>
          </div>

          <QuoteBreakdown q={quote} />

          <ul className="pp-fineprint">
            {quote.kind === 'upgrade' && <li>Your new plan and limits apply immediately after payment.</li>}
            {quote.kind === 'upgrade' && fromPlan?.status === 'trialing' && <li>Your free trial ends now and your paid period starts today.</li>}
            {isRenewal && <li>Your plan continues without interruption{new Date(quote.quote.periodStart) > new Date() ? ' — the new period starts when the current one ends' : ''}.</li>}
            <li>No auto-debit. You’ll renew manually before each period ends — we’ll remind you.</li>
            <li>Payments are processed securely by Cashfree (UPI, cards, net banking).</li>
          </ul>

          <label className="pp-agree">
            <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} disabled={stage === 'paying'} />
            <span>I authorise a one-time charge of <strong>{money(quote.quote.totalMinor, quote.quote.currency, { decimals: 2 })}</strong> for {KIND_LABEL[quote.kind]?.toLowerCase()} of the {quote.planName} plan.</span>
          </label>

          {stage === 'paying' && <div className="pp-center pp-center--inline"><Loader2 className="pp-spin" size={18} /> Opening secure payment…</div>}
        </div>
      )}

      {stage === 'verifying' && (
        <div className="pp-center" aria-live="polite">
          <Loader2 className="pp-spin" size={32} />
          <h4>Confirming your payment</h4>
          <p>This usually takes a few seconds. Please don’t close this window.</p>
        </div>
      )}

      {stage === 'success' && (
        <div className="pp-result" aria-live="polite">
          <CheckCircle2 size={52} className="pp-result__icon pp-result__icon--good" />
          <h4>{isRenewal ? 'Plan renewed!' : quote?.kind === 'upgrade' ? 'Upgrade complete!' : 'You’re subscribed!'}</h4>
          <p>
            {quote?.planName} is active{quote?.quote?.periodEnd ? ` until ${fmtDate(quote.quote.periodEnd)}` : ''}.
            {payment?.receiptNumber ? ` Receipt ${payment.receiptNumber} is available in Billing history.` : ''}
          </p>
          <div className="pp-result__actions"><button className="pp-btn pp-btn--primary" onClick={onClose}>Done</button></div>
        </div>
      )}

      {stage === 'pending' && (
        <div className="pp-result" aria-live="polite">
          <Clock size={46} className="pp-result__icon pp-result__icon--info" />
          <h4>Payment is being confirmed</h4>
          <p>Your bank hasn’t confirmed the payment yet. Your plan will update automatically once it does — you don’t need to pay again.{payment?.orderId ? ` Reference: ${payment.orderId}` : ''}</p>
          <div className="pp-result__actions"><button className="pp-btn pp-btn--primary" onClick={onClose}>Got it</button></div>
        </div>
      )}

      {stage === 'failed' && (
        <div className="pp-result" aria-live="polite">
          <XCircle size={46} className="pp-result__icon pp-result__icon--bad" />
          <h4>Payment not completed</h4>
          <p>The payment was cancelled or didn’t go through. If money was debited, it will be confirmed automatically or refunded by your bank.</p>
          <div className="pp-result__actions">
            <button className="pp-btn pp-btn--ghost" onClick={onClose}>Close</button>
            <button className="pp-btn pp-btn--primary" onClick={loadQuote}>Try again</button>
          </div>
        </div>
      )}

      {stage === 'review_needed' && (
        <div className="pp-result" aria-live="polite">
          <AlertTriangle size={46} className="pp-result__icon pp-result__icon--warn" />
          <h4>Payment received — under review</h4>
          <p>Your account changed while this payment was in progress, so we couldn’t apply it automatically. Our team will apply or refund it within 2 business days.{payment?.orderId ? ` Reference: ${payment.orderId}` : ''}</p>
          <div className="pp-result__actions"><button className="pp-btn pp-btn--primary" onClick={onClose}>OK</button></div>
        </div>
      )}
    </Modal>
  );
};

export default PlanCheckoutModal;
